import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import emailjs from "@emailjs/browser";
import Navbar from "./Navbar";
import Footer from "./Footer";

// Initialize EmailJS
emailjs.init("ns8iBr8YNGxt5OpPN");

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const ContactUs = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const contactInfoRef = useRef(null);
  const formRef = useRef(null);
  const mapRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    enquiryType: "",
    name: "",
    email: "",
    companyName: "",
    courseName: "",
    message: ""
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 80%",
            end: "bottom 20%",
          },
        }
      );

      gsap.fromTo(
        contactInfoRef.current,
        { opacity: 0, x: -100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          delay: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: contactInfoRef.current,
            start: "top 80%",
            end: "bottom 20%",
          },
        }
      );

      gsap.fromTo(
        formRef.current,
        { opacity: 0, x: 100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          delay: 0.4,
          ease: "power2.out",
          scrollTrigger: {
            trigger: formRef.current,
            start: "top 80%",
            end: "bottom 20%",
          },
        }
      );

      gsap.fromTo(
        mapRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.6,
          ease: "power2.out",
          scrollTrigger: {
            trigger: mapRef.current,
            start: "top 80%",
            end: "bottom 20%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 🔥 SEND ENQUIRY EMAIL
  const sendEnquiryEmail = async () => {
    try {
      const enquiryTypeLabels = {
        general: "General Feedback",
        course: "Course Details",
        corporate: "Corporate Training",
        partnership: "Partnership / Collaboration"
      };

      const fullEmailHTML = `
        <div style="font-family: system-ui, sans-serif; font-size:15px; color:#333; max-width:650px; margin:0 auto; background:#f9fff9; padding:30px; border-radius:15px; border:2px solid #0b9949;">

          <!-- LOGO -->
          <div style="text-align:center; margin-bottom:25px;">
            <div style="background:#0b9949; padding:20px; border-radius:8px;">
              <h1 style="color:white; margin:0; font-size:28px;">GREENMARK</h1>
              <p style="color:white; margin:5px 0 0 0; font-size:12px;">Training Institute</p>
            </div>
          </div>

          <!-- GREETING -->
          <p style="font-size:18px; margin-bottom:8px;">Hi <strong>GreenMark Team</strong>,</p>
          <p style="margin:0;">You have received a new enquiry through the website contact form.</p>

          <!-- ENQUIRY SUMMARY CARD -->
          <div style="background:white; padding:22px; border-radius:12px; margin:30px 0; box-shadow:0 4px 14px rgba(0,0,0,0.12); border-left:6px solid #0b9949;">
            <h3 style="color:#0b9949; margin-top:0; margin-bottom:15px;">📧 New Enquiry Received</h3>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
              <p style="margin:8px 0;"><strong>Enquiry Type:</strong> ${enquiryTypeLabels[formData.enquiryType] || formData.enquiryType}</p>
              <p style="margin:8px 0;"><strong>Name:</strong> ${formData.name}</p>
              <p style="margin:8px 0;"><strong>Email:</strong> ${formData.email}</p>
              <p style="margin:8px 0;"><strong>Company:</strong> ${formData.companyName || "N/A"}</p>
              <p style="margin:8px 0; grid-column: 1 / -1;"><strong>Course Interest:</strong> ${formData.courseName || "N/A"}</p>
            </div>
          </div>

          <!-- MESSAGE -->
          <h3 style="color:#0b9949; margin-top:25px;">💬 Message</h3>
          <div style="background:#f8f9fa; padding:15px; border-radius:8px; border:1px solid #e9ecef; margin-top:10px;">
            <p style="margin:0; line-height:1.6; white-space: pre-wrap;">${formData.message || "No message provided"}</p>
          </div>

          <!-- CONTACT INFO -->
          <h3 style="color:#0b9949; margin-top:30px;">📞 Contact Information</h3>
          <p style="margin:8px 0;"><strong>Email:</strong> ${formData.email}</p>
          <p style="margin:8px 0;"><strong>Name:</strong> ${formData.name}</p>
          ${formData.companyName ? `<p style="margin:8px 0;"><strong>Company:</strong> ${formData.companyName}</p>` : ''}

          <!-- FOOTER -->
          <p style="margin-top:35px; text-align:center; font-size:14px; color:#666;">
            Generated on: <strong>${new Date().toLocaleDateString("en-GB")} at ${new Date().toLocaleTimeString("en-GB")}</strong><br><br>
            This enquiry was submitted through the Greenmark website contact form.
          </p>

        </div>
      `;

      console.log("📧 Sending enquiry email...");

      // Send email using EmailJS
      const response = await emailjs.send(
        "service_7pytgxf",
        "template_lbjy8n3",
        {
          message_html: fullEmailHTML,
          to_email: "pcm@greenmark.com.sg", // Replace with actual Greensafe email
          reply_to: formData.email || "noreply@greensafe.sg",
          subject: `New Enquiry: ${enquiryTypeLabels[formData.enquiryType] || formData.enquiryType} - ${formData.name}`
        }
      );

      console.log("✅ Enquiry email sent successfully:", response);
      return true;
    } catch (error) {
      console.error("❌ Enquiry email sending failed:", error);
      throw error;
    }
  };

  // 🔥 SUCCESS MODAL COMPONENT
  const SuccessModal = ({ show, onClose }) => {
    if (!show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">Enquiry Sent Successfully!</h2>
          <p className="text-gray-700 mb-2">
            <strong>Thank you for contacting us!</strong>
          </p>
          <p className="text-gray-600 mb-4">
            We have received your enquiry and will get back to you within 24 hours.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-sm text-green-700">
            <p><strong>What happens next?</strong></p>
            <p>• Our team will review your enquiry</p>
            <p>• We'll contact you via email or phone</p>
            <p>• You'll receive a response within 24 hours</p>
          </div>
          <button
            onClick={onClose}
            className="bg-[#0b9949] text-white px-6 py-2 rounded-md hover:bg-[#087c3a] transition-all w-full"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.enquiryType || !formData.name || !formData.email || !formData.message) {
      alert("❌ Please fill all required fields!");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("📧 Sending enquiry...");
      // Send enquiry email
      await sendEnquiryEmail();

      // Show success modal
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        enquiryType: "",
        name: "",
        email: "",
        companyName: "",
        courseName: "",
        message: ""
      });

    } catch (error) {
      console.error("❌ Enquiry submission error:", error);
      alert(`❌ Error: ${error.message || 'Failed to send enquiry. Please try again or contact us directly.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
  };

  return (
    <div className="bg-white text-gray-900 font-sans overflow-x-hidden scroll-smooth">
      <Navbar />

      <section
        ref={sectionRef}
        className="relative overflow-hidden bg-white text-gray-800 mt-32 md:mt-31 lg:mt-31"
      >
        {/* Parallax Background */}
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url('./contactus.avif')",
            backgroundAttachment: "fixed",
          }}
        ></div>
        <div className="absolute inset-0 bg-[#0b9949]/75"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-32">

          {/* Header */}
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white mb-16"
          >
            <h1 className="text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Have questions or need help with our courses?
              Our <span className="font-semibold">GreenMark Team</span> is ready to assist you!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">

            {/* Contact Info */}
            <motion.div
              ref={contactInfoRef}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl p-10 shadow-xl"
            >
              <h2 className="text-2xl font-semibold text-[#0b9949] mb-6">
                Get in Touch
              </h2>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Phone className="text-[#0b9949]" size={24} />
                  <p className="text-gray-700 font-medium">+65 8014 7799</p>
                </div>

                <div className="flex items-center space-x-4">
                  <Mail className="text-[#0b9949]" size={24} />
                  <p className="text-gray-700 font-medium">pcm@greenmark.com.sg</p>
                </div>

                <div className="flex items-center space-x-4">
                  <MapPin className="text-[#0b9949]" size={24} />
                  <p className="text-gray-700 font-medium">
                    Pioneer Centre, #04-44, Singapore
                  </p>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-10">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Business Hours
                </h3>
                <p className="text-gray-600">
                  All Days: 9:00 AM – 8:00 PM
                </p>
              </div>

              {/* Response Time */}
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2">📞 Quick Response</h4>
                <p className="text-sm text-green-700">
                  We typically respond to enquiries within 2-4 hours during business hours.
                </p>
              </div>
            </motion.div>

            {/* Enquiry Form */}
            <motion.form
              ref={formRef}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl p-10 shadow-xl"
              onSubmit={handleSubmit}
            >
              <h2 className="text-2xl font-semibold text-[#0b9949] mb-6">
                Enquiry Form
              </h2>

              <div className="space-y-4">
                <select
                  name="enquiryType"
                  className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-[#0b9949] focus:border-[#0b9949]"
                  value={formData.enquiryType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Enquiry Type *</option>
                  <option value="general">General Feedback</option>
                  <option value="course">Course Details</option>
                  <option value="corporate">Corporate Training</option>
                  <option value="partnership">Partnership / Collaboration</option>
                </select>

                <input
                  type="text"
                  name="name"
                  placeholder="Your Name *"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-[#0b9949] focus:border-[#0b9949]"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email *"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-[#0b9949] focus:border-[#0b9949]"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-[#0b9949] focus:border-[#0b9949]"
                  value={formData.companyName}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="courseName"
                  placeholder="Course Name (if applicable)"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-[#0b9949] focus:border-[#0b9949]"
                  value={formData.courseName}
                  onChange={handleInputChange}
                />

                <textarea
                  name="message"
                  placeholder="Your Message *"
                  rows="5"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-[#0b9949] focus:border-[#0b9949]"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-6 w-full bg-[#0b9949] text-white py-3 rounded-lg font-semibold hover:bg-[#098b43] hover:scale-105 transition-transform disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? "⏳ Sending..." : "Submit Enquiry"}
              </button>

              <p className="text-xs text-gray-500 mt-3 text-center">
                * Required fields
              </p>
            </motion.form>
          </div>
        </div>

        {/* Google Map */}
        <div ref={mapRef} className="relative z-10 w-full h-[400px] md:h-[500px] lg:h-[600px]">
          <iframe
            title="Greensafe Pioneer Centre"
            src="https://www.google.com/maps?q=Pioneer+Centre+Singapore&output=embed"
            className="w-full h-full border-0"
            allowFullScreen
            loading="lazy"
          ></iframe>
        </div>
      </section>

      {/* Success Modal */}
      <SuccessModal show={showSuccessModal} onClose={handleModalClose} />

      <Footer />
    </div>
  );
};

export default ContactUs;